import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Target,
  TrendingUp,
  TrendingDown,
  Wallet,
  Calendar,
  Zap,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronRight,
  RefreshCw,
  PoundSterling,
  Percent,
  Clock,
  Brain,
  LineChart,
  ArrowUpRight,
  ArrowDownRight,
  Trophy,
  Flame,
  Shield,
  DollarSign,
  BarChart3,
  Sparkles
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { MarkdownText } from "@/components/MarkdownText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RISK_LEVELS = {
  conservative: { label: "Conservative", icon: Shield, color: "text-green-500", description: "Lower risk, steady growth" },
  moderate: { label: "Moderate", icon: BarChart3, color: "text-blue-500", description: "Balanced risk/reward" },
  aggressive: { label: "Aggressive", icon: Flame, color: "text-orange-500", description: "Higher risk, higher potential" }
};

const PRESET_AMOUNTS = [100, 250, 500, 1000, 2500, 5000, 10000, 25000, 50000];

export const WealthChallenge = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [activeChallenge, setActiveChallenge] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [dailyBrief, setDailyBrief] = useState(null);
  const [tradeSignals, setTradeSignals] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Setup form
  const [setupStep, setSetupStep] = useState(1);
  const [startingAmount, setStartingAmount] = useState(5000);
  const [targetAmount, setTargetAmount] = useState(10000);
  const [months, setMonths] = useState(12);
  const [riskTolerance, setRiskTolerance] = useState("moderate");
  const [challengeName, setChallengeName] = useState("My Wealth Challenge");
  const [creating, setCreating] = useState(false);
  
  // Investment advice
  const [adviceAmount, setAdviceAmount] = useState(1000);
  const [advice, setAdvice] = useState(null);
  const [loadingAdvice, setLoadingAdvice] = useState(false);

  useEffect(() => {
    fetchActiveChallenge();
  }, []);

  const fetchActiveChallenge = async () => {
    try {
      const response = await axios.get(`${API}/challenge/active`, { timeout: 10000 });
      if (response.data.active && response.data.challenge) {
        setActiveChallenge(response.data.challenge);
        // Fetch daily brief and signals in background (don't await)
        fetchDailyBrief();
        fetchTradeSignals();
      }
    } catch (error) {
      console.error("Error fetching challenge:", error);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const fetchDailyBrief = async () => {
    try {
      const response = await axios.get(`${API}/challenge/daily-brief`, { timeout: 60000 });
      if (response.data.has_challenge) {
        setDailyBrief(response.data.brief);
      }
    } catch (error) {
      console.error("Error fetching daily brief:", error);
      setDailyBrief({ ai_brief: "Brief loading... The AI is generating your personalized trading plan. This may take up to 60 seconds.", model: "loading" });
    }
  };

  const fetchTradeSignals = async () => {
    try {
      const response = await axios.get(`${API}/challenge/trade-signals`, { timeout: 60000 });
      if (response.data.has_challenge) {
        setTradeSignals(response.data);
      }
    } catch (error) {
      console.error("Error fetching trade signals:", error);
    }
  };

  const createChallenge = async () => {
    try {
      setCreating(true);
      const response = await axios.post(`${API}/challenge/create`, {
        starting_amount: startingAmount,
        target_amount: targetAmount,
        months: months,
        risk_tolerance: riskTolerance,
        name: challengeName
      });
      
      setActiveChallenge(response.data);
      setShowSetup(false);
      toast.success("Challenge created! Let's hit that target! 🎯");
      
      // Fetch brief and signals
      setTimeout(() => {
        fetchDailyBrief();
        fetchTradeSignals();
      }, 1000);
      
    } catch (error) {
      console.error("Error creating challenge:", error);
      toast.error(error.response?.data?.detail || "Failed to create challenge");
    } finally {
      setCreating(false);
    }
  };

  const getInvestmentAdvice = async (amount) => {
    try {
      setLoadingAdvice(true);
      const response = await axios.get(`${API}/challenge/investment-advice/${amount}`);
      setAdvice(response.data);
    } catch (error) {
      console.error("Error getting advice:", error);
      toast.error("Failed to get investment advice");
    } finally {
      setLoadingAdvice(false);
    }
  };

  const refreshBrief = async () => {
    setRefreshing(true);
    await fetchDailyBrief();
    await fetchTradeSignals();
    setRefreshing(false);
    toast.success("Brief refreshed!");
  };

  const returnPct = ((targetAmount / startingAmount) - 1) * 100;
  const monthlyReturn = ((targetAmount / startingAmount) ** (1 / months) - 1) * 100;

  if (loading || initialLoad) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Loading your challenge...</p>
        </div>
      </div>
    );
  }

  // Setup Wizard
  if (showSetup || !activeChallenge) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2">
            <Trophy className="w-8 h-8 text-amber-500" />
            <h1 className="text-3xl font-bold">AI Wealth Challenge</h1>
          </div>
          <p className="text-muted-foreground">Set your target. AI becomes your hedge fund manager.</p>
        </div>

        <Tabs defaultValue="challenge" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="challenge">Start a Challenge</TabsTrigger>
            <TabsTrigger value="advice">Get Investment Advice</TabsTrigger>
          </TabsList>

          <TabsContent value="challenge" className="space-y-6 mt-6">
            {/* Step 1: Amount */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PoundSterling className="w-5 h-5 text-primary" />
                  How much are you starting with?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {PRESET_AMOUNTS.map((amount) => (
                    <Button
                      key={amount}
                      variant={startingAmount === amount ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        setStartingAmount(amount);
                        setTargetAmount(amount * 2); // Default 2x target
                      }}
                    >
                      £{amount.toLocaleString()}
                    </Button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Label>Custom:</Label>
                  <Input
                    type="number"
                    value={startingAmount}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      setStartingAmount(val);
                      setTargetAmount(Math.max(targetAmount, val + 100));
                    }}
                    className="w-32"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Step 2: Target */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  What is your target?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {[1.25, 1.5, 1.75, 2, 2.5, 3].map((multiplier) => (
                    <Button
                      key={multiplier}
                      variant={targetAmount === Math.round(startingAmount * multiplier) ? "default" : "outline"}
                      size="sm"
                      onClick={() => setTargetAmount(Math.round(startingAmount * multiplier))}
                    >
                      £{Math.round(startingAmount * multiplier).toLocaleString()} ({((multiplier - 1) * 100).toFixed(0)}%)
                    </Button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Label>Custom target:</Label>
                  <Input
                    type="number"
                    value={targetAmount}
                    onChange={(e) => setTargetAmount(parseInt(e.target.value) || startingAmount + 100)}
                    className="w-32"
                  />
                </div>
                
                <div className="p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Target Return:</span>
                    <span className={`font-bold ${returnPct > 100 ? 'text-orange-500' : returnPct > 50 ? 'text-amber-500' : 'text-green-500'}`}>
                      +{returnPct.toFixed(0)}%
                    </span>
                  </div>
                  {returnPct > 100 && (
                    <div className="flex items-center gap-2 mt-2 text-orange-500 text-sm">
                      <AlertTriangle className="w-4 h-4" />
                      Very aggressive target - requires active trading
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Step 3: Timeframe */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  How long do you have?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {[3, 6, 12, 18, 24, 36].map((m) => (
                    <Button
                      key={m}
                      variant={months === m ? "default" : "outline"}
                      size="sm"
                      onClick={() => setMonths(m)}
                    >
                      {m} months
                    </Button>
                  ))}
                </div>
                
                <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Required Monthly Return:</span>
                    <span className={`font-bold ${monthlyReturn > 8 ? 'text-orange-500' : monthlyReturn > 5 ? 'text-amber-500' : 'text-green-500'}`}>
                      +{monthlyReturn.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Difficulty:</span>
                    <Badge variant={monthlyReturn > 8 ? "destructive" : monthlyReturn > 5 ? "warning" : "success"}>
                      {monthlyReturn > 8 ? "Very Hard" : monthlyReturn > 5 ? "Challenging" : "Achievable"}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Step 4: Risk */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  Risk Tolerance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(RISK_LEVELS).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <button
                        key={key}
                        onClick={() => setRiskTolerance(key)}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          riskTolerance === key 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <Icon className={`w-6 h-6 mx-auto mb-2 ${config.color}`} />
                        <div className="font-medium">{config.label}</div>
                        <div className="text-xs text-muted-foreground">{config.description}</div>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Challenge Name */}
            <Card>
              <CardHeader>
                <CardTitle>Name Your Challenge</CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  value={challengeName}
                  onChange={(e) => setChallengeName(e.target.value)}
                  placeholder="e.g., £5K to £10K Challenge"
                />
              </CardContent>
            </Card>

            {/* Summary & Create */}
            <Card className="border-primary">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  Challenge Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">£{startingAmount.toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">Starting</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold text-primary">£{targetAmount.toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">Target</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold text-green-500">+{returnPct.toFixed(0)}%</div>
                    <div className="text-xs text-muted-foreground">Return</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{months}</div>
                    <div className="text-xs text-muted-foreground">Months</div>
                  </div>
                </div>

                <Button 
                  className="w-full" 
                  size="lg"
                  onClick={createChallenge}
                  disabled={creating}
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating Challenge...
                    </>
                  ) : (
                    <>
                      <Trophy className="w-4 h-4 mr-2" />
                      Start Challenge
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="advice" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-primary" />
                  AI Investment Advice
                </CardTitle>
                <CardDescription>
                  Tell me how much you have, and AI will tell you exactly what to do
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {PRESET_AMOUNTS.map((amount) => (
                    <Button
                      key={amount}
                      variant={adviceAmount === amount ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        setAdviceAmount(amount);
                        getInvestmentAdvice(amount);
                      }}
                    >
                      £{amount.toLocaleString()}
                    </Button>
                  ))}
                </div>
                
                <div className="flex gap-2">
                  <Input
                    type="number"
                    value={adviceAmount}
                    onChange={(e) => setAdviceAmount(parseInt(e.target.value) || 100)}
                    placeholder="Enter amount"
                    className="flex-1"
                  />
                  <Button onClick={() => getInvestmentAdvice(adviceAmount)} disabled={loadingAdvice}>
                    {loadingAdvice ? <Loader2 className="w-4 h-4 animate-spin" /> : "Get Advice"}
                  </Button>
                </div>

                {advice && (
                  <div className="mt-4 space-y-4">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{advice.tier}</Badge>
                      <span className="text-sm text-muted-foreground">{advice.strategy?.description}</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-blue-500/10 rounded-lg">
                        <div className="text-sm text-muted-foreground">Core Holdings</div>
                        <div className="text-xl font-bold">{advice.strategy?.core_pct}%</div>
                      </div>
                      <div className="p-3 bg-orange-500/10 rounded-lg">
                        <div className="text-sm text-muted-foreground">Trading</div>
                        <div className="text-xl font-bold">{advice.strategy?.trading_pct || 0}%</div>
                      </div>
                    </div>
                    
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <div className="text-sm font-medium mb-2">AI Recommendation:</div>
                      <MarkdownText>{advice.ai_advice}</MarkdownText>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // Active Challenge Dashboard
  const progress = activeChallenge.progress_pct || 0;
  const isOnTrack = activeChallenge.is_on_track;
  const variance = activeChallenge.variance || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Trophy className="w-6 h-6 text-amber-500" />
            {activeChallenge.name}
          </h1>
          <p className="text-muted-foreground">
            {activeChallenge.days_remaining} days remaining
          </p>
        </div>
        <Button variant="outline" onClick={refreshBrief} disabled={refreshing}>
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Progress Card */}
      <Card className={isOnTrack ? "border-green-500/50" : "border-orange-500/50"}>
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="text-center">
              <div className="text-sm text-muted-foreground">Started With</div>
              <div className="text-xl font-bold">£{activeChallenge.starting_amount?.toLocaleString()}</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-muted-foreground">Current Value</div>
              <div className="text-xl font-bold text-primary">£{activeChallenge.current_value?.toLocaleString()}</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-muted-foreground">Target</div>
              <div className="text-xl font-bold">£{activeChallenge.target_amount?.toLocaleString()}</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-muted-foreground">Return</div>
              <div className={`text-xl font-bold ${activeChallenge.current_return_pct >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {activeChallenge.current_return_pct >= 0 ? '+' : ''}{activeChallenge.current_return_pct?.toFixed(1)}%
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-muted-foreground">Status</div>
              <Badge variant={isOnTrack ? "success" : "warning"} className="mt-1">
                {isOnTrack ? "On Track ✓" : "Behind Schedule"}
              </Badge>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progress to goal</span>
              <span>{progress.toFixed(1)}%</span>
            </div>
            <Progress value={progress} className="h-3" />
          </div>

          {variance !== 0 && (
            <div className={`mt-4 p-3 rounded-lg ${variance >= 0 ? 'bg-green-500/10' : 'bg-orange-500/10'}`}>
              <div className="flex items-center gap-2">
                {variance >= 0 ? (
                  <ArrowUpRight className="w-4 h-4 text-green-500" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 text-orange-500" />
                )}
                <span className={variance >= 0 ? 'text-green-500' : 'text-orange-500'}>
                  £{Math.abs(variance).toLocaleString()} {variance >= 0 ? 'ahead of' : 'behind'} schedule
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Daily Brief */}
      {dailyBrief && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-primary" />
              Daily AI Brief
              <Badge variant="outline" className="ml-2">GPT-5.2</Badge>
            </CardTitle>
            <CardDescription>
              Generated at {new Date(dailyBrief.generated_at).toLocaleTimeString()}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-muted/50 rounded-lg">
              <MarkdownText>{dailyBrief.ai_brief}</MarkdownText>
            </div>
            
            {dailyBrief.challenge_status && (
              <div className="mt-4 p-3 bg-primary/5 rounded-lg">
                <div className="text-sm font-medium mb-1">Weekly Target:</div>
                <div className="text-lg font-bold">
                  Make £{dailyBrief.challenge_status.weekly_target?.toLocaleString()} this week
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Trade Signals */}
      {tradeSignals && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              AI Trade Signals
            </CardTitle>
            <CardDescription>
              Position sizes based on your £{tradeSignals.trading_capital?.toLocaleString()} trading capital
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-3 bg-amber-500/10 rounded-lg mb-4">
              <div className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Max risk per trade: £{tradeSignals.max_risk_per_trade?.toFixed(0)} (2% of trading capital)
                </span>
              </div>
            </div>
            
            <MarkdownText>{tradeSignals.signals_text}</MarkdownText>
          </CardContent>
        </Card>
      )}

      {/* Allocation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Your Allocation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-blue-500/10 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Wallet className="w-4 h-4 text-blue-500" />
                <span className="font-medium">Core Holdings</span>
              </div>
              <div className="text-2xl font-bold">
                £{activeChallenge.allocations?.core_holdings?.amount?.toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground">
                {activeChallenge.allocations?.core_holdings?.target_pct}% of portfolio
              </div>
            </div>
            
            <div className="p-4 bg-orange-500/10 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-orange-500" />
                <span className="font-medium">Active Trading</span>
              </div>
              <div className="text-2xl font-bold">
                £{activeChallenge.allocations?.active_trading?.amount?.toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground">
                {activeChallenge.allocations?.active_trading?.target_pct}% of portfolio
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI Strategy */}
      {activeChallenge.ai_strategy && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              AI Strategy
            </CardTitle>
          </CardHeader>
          <CardContent>
            <MarkdownText>{activeChallenge.ai_strategy.strategy_text}</MarkdownText>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Button variant="outline" className="h-auto py-4" onClick={() => navigate('/aura-xauusd')}>
          <div className="text-center">
            <DollarSign className="w-6 h-6 mx-auto mb-1 text-amber-500" />
            <div className="text-sm">Gold Trades</div>
          </div>
        </Button>
        <Button variant="outline" className="h-auto py-4" onClick={() => navigate('/aura-eurusd')}>
          <div className="text-center">
            <LineChart className="w-6 h-6 mx-auto mb-1 text-blue-500" />
            <div className="text-sm">EUR/USD</div>
          </div>
        </Button>
        <Button variant="outline" className="h-auto py-4" onClick={() => navigate('/portfolios')}>
          <div className="text-center">
            <Wallet className="w-6 h-6 mx-auto mb-1 text-green-500" />
            <div className="text-sm">My Funds</div>
          </div>
        </Button>
        <Button variant="outline" className="h-auto py-4" onClick={() => navigate('/deep-research')}>
          <div className="text-center">
            <Brain className="w-6 h-6 mx-auto mb-1 text-purple-500" />
            <div className="text-sm">Research</div>
          </div>
        </Button>
      </div>
    </div>
  );
};

export default WealthChallenge;
